import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, Lightning } from "@phosphor-icons/react";
import axios from "axios";
import InstallPrompt from "@/components/InstallPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicFunnel() {
  const { slug } = useParams();
  const [funnel, setFunnel] = useState(null);
  const [err, setErr] = useState("");
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/f/${slug}`)
      .then(r => setFunnel(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Funnel not found"));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="text-xs font-mono-k tracking-widest-k text-red-400 mb-2">— 404</div>
          <div className="font-display text-3xl mb-2">Funnel not found</div>
          <div className="text-sm font-mono-k text-[#A1A1AA]">{err}</div>
        </div>
      </div>
    );
  }
  if (!funnel) return <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center font-mono-k">Loading…</div>;

  const steps = funnel.steps || [];
  const step = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;
  const theme = funnel.owner?.theme_config || {};
  const accent = theme.accent_color || "#FACC15";
  const displayFont = theme.font_display || "Clash Display";

  const next = async () => {
    if (isLast) {
      await axios.post(`${API}/public/f/${slug}/complete`, { step_order: step.order }).catch(() => {});
      setDone(true);
      return;
    }
    setStepIdx(i => i + 1);
  };

  const submitForm = async () => {
    const need = step.form_fields?.length ? step.form_fields : ["name", "email"];
    if (need.includes("name") && !form.name.trim()) return toast.error("Name is required");
    if (need.includes("email") && !form.email.trim()) return toast.error("Email is required");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/f/${slug}/lead`, {
        name: form.name || "(no name)",
        email: form.email,
        phone: form.phone || "",
        message: form.message || "",
        step_order: step.order,
        variant: funnel.variant,
      });
      toast.success("Got it — moving on");
      if (isLast) {
        await axios.post(`${API}/public/f/${slug}/complete`, { step_order: step.order }).catch(() => {});
        setDone(true);
      } else {
        setStepIdx(i => i + 1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center p-6" data-testid="funnel-done">
        <div className="max-w-md text-center">
          <div className="mb-4 flex justify-center"><CheckCircle size={64} weight="fill" style={{ color: accent }} /></div>
          <div className="font-display text-4xl mb-3" style={{ fontFamily: displayFont }}>You're all set.</div>
          <div className="text-sm font-mono-k text-[#A1A1AA] mb-6">{funnel.owner?.name || "The creator"} will be in touch shortly.</div>
          {funnel.owner?.slug && (
            <a href={`/u/${funnel.owner.slug}`} className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 hover:bg-white/5 font-mono-k text-sm tracking-widest-k" data-testid="funnel-visit-bio">
              VISIT {funnel.owner?.name?.toUpperCase() || "PROFILE"} <ArrowRight size={16} />
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white" data-testid="public-funnel">
      <InstallPrompt />
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-white/5 z-50">
        <div className="h-full transition-all duration-500" style={{ width: `${((stepIdx + 1) / steps.length) * 100}%`, backgroundColor: accent }} data-testid="funnel-progress" />
      </div>

      {/* Header */}
      <div className="max-w-2xl mx-auto px-6 pt-16 pb-4 flex items-center gap-3">
        {funnel.owner?.avatar && <img src={funnel.owner.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />}
        <div className="min-w-0">
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">STEP {stepIdx + 1} / {steps.length}</div>
          <div className="text-xs font-mono-k truncate">{funnel.owner?.name || funnel.name}</div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-6 py-10">
        {step.image && <img src={step.image} alt="" className="w-full max-h-64 object-cover mb-8 border border-white/10" />}

        <div className="text-xs font-mono-k tracking-widest-k mb-3" style={{ color: accent }} data-testid={`funnel-view-step-type-${stepIdx}`}>
          — {(step.type || "landing").toUpperCase().replace("-", " ")}
        </div>

        <h1 className="font-display leading-tight text-4xl sm:text-5xl lg:text-6xl mb-4" style={{ fontFamily: displayFont }} data-testid="funnel-title">
          {step.title}
        </h1>

        {step.subtitle && (
          <p className="text-base sm:text-lg text-[#DDDDDD] mb-8 leading-relaxed" data-testid="funnel-subtitle">
            {step.subtitle}
          </p>
        )}

        {step.type === "lead-form" ? (
          <div className="space-y-3">
            {(step.form_fields?.length ? step.form_fields : ["name", "email"]).includes("name") && (
              <input placeholder="Your name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-[color:var(--accent)] px-4 py-3 font-mono-k text-sm outline-none" style={{ "--accent": accent }} data-testid="funnel-input-name" />
            )}
            {(step.form_fields?.length ? step.form_fields : ["name", "email"]).includes("email") && (
              <input type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-[color:var(--accent)] px-4 py-3 font-mono-k text-sm outline-none" style={{ "--accent": accent }} data-testid="funnel-input-email" />
            )}
            {(step.form_fields || []).includes("phone") && (
              <input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 font-mono-k text-sm outline-none" data-testid="funnel-input-phone" />
            )}
            {(step.form_fields || []).includes("message") && (
              <textarea placeholder="Tell us more…" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} className="w-full bg-transparent border border-white/20 px-4 py-3 font-mono-k text-sm outline-none" data-testid="funnel-input-message" />
            )}
            <button onClick={submitForm} disabled={submitting} className="w-full flex items-center justify-center gap-2 py-4 font-mono-k text-sm tracking-widest-k transition-all hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: accent, color: "#0A0D14" }} data-testid="funnel-submit">
              <Lightning size={16} weight="fill" /> {submitting ? "SENDING…" : (step.cta_label || "CONTINUE")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={next} className="flex-1 flex items-center justify-center gap-2 py-4 font-mono-k text-sm tracking-widest-k transition-all hover:opacity-90" style={{ backgroundColor: accent, color: "#0A0D14" }} data-testid="funnel-cta">
              {step.cta_label || (isLast ? "FINISH" : "CONTINUE")} <ArrowRight size={16} />
            </button>
            {step.cta_url && (
              <a href={step.cta_url} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 py-4 font-mono-k text-sm tracking-widest-k border border-white/20 hover:bg-white/5" data-testid="funnel-cta-external">
                OPEN LINK <ArrowRight size={16} />
              </a>
            )}
          </div>
        )}
      </main>

      <footer className="max-w-2xl mx-auto px-6 py-10 text-center">
        <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">
          Powered by <a href="/" className="text-[#FACC15] hover:underline">Korporate</a>
        </div>
      </footer>
    </div>
  );
}
